import { createRouter, createWebHistory } from 'vue-router'

import logger from '../logger'
import { useSessionStore } from '../stores/session'
import { useUserStore } from '../stores/user'

import SearchView from '../views/SearchView.vue'
import FeaturedView from '../views/FeaturedView.vue'
import GetStartedView from '../views/GetStartedView.vue'
import UpdatedTermsView from '../views/UpdatedTermsView.vue'
import LogoutView from '../views/LogoutView.vue'
import SubmitReportView from '../views/SubmitReportView.vue'
import UserAccountView from '../views/UserAccountView.vue'
import TakedownNoticeView from '../views/TakedownNoticeView.vue'
import UnsubscribeView from '../views/UnsubscribeView.vue'
import ErrorView from '../views/ErrorView.vue'
import NotFoundView from '../views/NotFoundView.vue'
import ProblemReportView from '../views/ProblemReportView.vue'
import ExploreView from '../views/ExploreView.vue'
import CreatorIncidentsView from '../views/CreatorIncidentsView.vue'
import RecordedCrashView from '../views/RecordedCrashView.vue'
import PreviewIncidentView from '../views/PreviewIncidentView.vue'
import SubmitBountyView from '../views/SubmitBountyView.vue'
import TeslaCamView from '../views/TeslaCamView.vue'
import UserBountiesView from '../views/UserBountiesView.vue'
import PublicBountiesView from '../views/PublicBountiesView.vue'

// Staff views
import IncidentReportsView from '../views/moderation/IncidentReportsView.vue'
import BountiesView from '../views/moderation/BountiesView.vue'
import UsersView from '../views/moderation/UsersView.vue'
import ModerationDashboard from '../views/moderation/ModerationDashboard.vue'
import UserFilesView from '../views/moderation/UserFilesView.vue'
import { storeToRefs } from 'pinia'

// meta.auth levels (absent = anonymous session sufficient):
//   'user'  — requires an Auth0 login (any authenticated user)
//   'staff' — requires Staff role (implies login)
// meta.isPublic — no session required at all (takedown form, logout callback, etc.)
// meta.requiresCurrentTerms — a signed-in user who has not accepted the current terms is sent to /terms-update first

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: { name: 'explore' }
    },
    {
      path: '/featured',
      name: 'featured',
      component: FeaturedView,
      meta: { title: 'Featured' }
    },
    {
      path: '/search',
      name: 'search',
      component: SearchView,
      meta: { title: 'Search' }
    },
    {
      path: '/error',
      name: 'error',
      component: ErrorView,
      meta: { title: 'Something went wrong', isPublic: true }
    },
    {
      path: '/get-started',
      name: 'get-started',
      component: GetStartedView,
      meta: { title: 'Get Started', auth: 'User' }
    },
    {
      path: '/terms-update',
      name: 'updated-terms',
      component: UpdatedTermsView,
      meta: { title: 'Updated terms', auth: 'User' }
    },
    {
      path: '/explore',
      name: 'explore',
      component: ExploreView,
      meta: { title: 'Explore' }
    },
    {
      path: '/preview/:incidentReportId',
      name: 'preview',
      component: PreviewIncidentView,
      meta: { title: 'Preview Incident' }
    },
    {
      path: '/crash/:id',
      name: 'recorded-crash',
      component: RecordedCrashView,
      meta: { title: 'Recorded crash', isPublic: true }
    },
    {
      path: '/share',
      name: 'upload',
      component: SubmitReportView,
      meta: { auth: 'User', requiresCurrentTerms: true }
    },
    {
      path: '/bounty/submit',
      name: 'submit-bounty',
      component: SubmitBountyView,
      meta: { title: 'Post a Bounty', auth: 'User', requiresCurrentTerms: true }
    },
    {
      path: '/teslacam',
      name: 'teslacam',
      component: TeslaCamView,
      meta: { title: 'TeslaCam', auth: 'User', requiresCurrentTerms: true }
    },
    {
      path: '/uploads',
      name: 'uploads',
      component: CreatorIncidentsView,
      meta: { auth: 'User' }
    },
    {
      path: '/bounties/mine',
      name: 'my-bounties',
      component: UserBountiesView,
      meta: { title: 'Your Bounties', auth: 'User' }
    },
    {
      path: '/bounties',
      name: 'bounties',
      component: PublicBountiesView,
      meta: { title: 'Bounties' }
    },
    {
      path: '/account',
      name: 'account',
      component: UserAccountView,
      meta: { auth: 'User' }
    },
    {
      path: '/request-removal',
      name: 'request-removal',
      component: TakedownNoticeView,
      meta: { title: 'Takedown Notice Request Form', isPublic: true }
    },
    {
      path: '/report-a-problem',
      name: 'report-a-problem',
      component: ProblemReportView,
      meta: { title: 'Report a problem', isPublic: true }
    },
    {
      path: '/unsubscribe',
      name: 'unsubscribe',
      component: UnsubscribeView,
      meta: { title: 'Unsubscribe', isPublic: true }
    },
    {
      path: '/logout-callback',
      name: 'logout',
      component: LogoutView,
      meta: { title: 'Sign out', isPublic: true }
    },
    {
      path: '/moderation',
      name: 'moderation-dashboard',
      component: ModerationDashboard,
      meta: { title: 'Moderation Dashboard', auth: 'Staff' }
    },
    {
      path: '/moderation/incident-reports',
      name: 'incident-reports',
      component: IncidentReportsView,
      meta: { title: 'Incident Reports', auth: 'Staff' }
    },
    {
      path: '/moderation/bounties',
      name: 'moderation-bounties',
      component: BountiesView,
      meta: { title: 'Bounties', auth: 'Staff' }
    },
    {
      path: '/moderation/users',
      name: 'users',
      component: UsersView,
      meta: { title: 'Users', auth: 'Staff' }
    },
    {
      path: '/moderation/files',
      name: 'files',
      component: UserFilesView,
      meta: { title: 'Files', auth: 'Staff' }
    },
    // Must stay last. The URL is kept as typed.
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: NotFoundView,
      meta: { title: 'Page not found', isPublic: true }
    }
  ]
})

// Register router guard. Wait for Auth0 to finish loading before checking authentication.
router.beforeEach(async (to) => {
  const store = useSessionStore()
  const { isLoggingIn, isLoggedIn, role } = storeToRefs(store)

  // Public routes need no session at all
  if (to.meta.isPublic) return true

  // Staff routes: block until Auth0 has fully initialised, then verify role
  if (to.meta.auth === 'Staff') {
    logger.debug({ role: role.value }, 'Route requires Staff auth')

    // Wait for Auth0 SDK init/callback — NOT isAuthenticated, which is also true for anonymous sessions
    const AUTH0_INIT_TIMEOUT_MS = 8000
    const waitStart = Date.now()
    while (isLoggingIn.value) {
      if (Date.now() - waitStart > AUTH0_INIT_TIMEOUT_MS) {
        logger.error('Auth0 initialization timed out — redirecting to explore')
        return { name: 'explore' }
      }
      logger.debug('Waiting for Auth0 to initialise...')
      await new Promise((resolve) => setTimeout(resolve, 50))
    }

    if (isLoggedIn.value && role.value === 'Staff') return true

    // Auth0-authenticated but not Staff
    if (isLoggedIn.value) return { name: 'explore' }

    // Not Auth0-authenticated — initiate login and preserve the intended destination
    await store.loginWithRedirect({ appState: { target: to.fullPath } })
    return false
  }

  // Routes that write (share footage, post a bounty) need the current terms. The API refuses the writes too; this just
  // saves the user filling in a form that cannot be sent.
  if (to.meta.requiresCurrentTerms && useUserStore().termsUpdateRequired) {
    return { name: 'updated-terms', query: { redirect: to.fullPath } }
  }

  // auth: 'User' and unauthenticated routes: allow navigation and let App.vue's
  // Turnstile challenge handle anonymous session establishment.
  return true
})
